import type { AccessCatalogItemPublic, AccessNodePublic } from '@accounts/shared';

import { STR } from '../../../../i18n';

/**
 * Pure helpers over the hierarchy as the server returns it. The server is the authority
 * on placement (a node sits at a deeper level than its parent; no cycles) — these only
 * shape what the screens draw and which choices a picker offers, so a choice the server
 * would refuse is not offered in the first place.
 */

export function liveNodes(nodes: readonly AccessNodePublic[]): AccessNodePublic[] {
  return nodes.filter((node) => node.retiredAt === null);
}

function byName(a: { name: string }, b: { name: string }): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

/** Live children of a node (or the live roots when `parentId` is null), by name. */
export function childrenOf(
  nodes: readonly AccessNodePublic[],
  parentId: string | null,
): AccessNodePublic[] {
  return liveNodes(nodes)
    .filter((node) => node.parentId === parentId)
    .sort(byName);
}

/** The ids on the way from the root to `nodeId`, inclusive — empty for null. */
export function chainOf(nodes: readonly AccessNodePublic[], nodeId: string | null): string[] {
  if (nodeId === null) return [];
  const node = nodes.find((candidate) => candidate.id === nodeId);
  if (node === undefined) return [];
  return node.path.map((step) => step.id);
}

/**
 * The parents a node at `depth` may be moved or added under: live, at a shallower level.
 * A node's own subtree is always deeper than it, so it can never appear here.
 */
export function parentChoices(
  nodes: readonly AccessNodePublic[],
  depth: number,
): AccessNodePublic[] {
  return liveNodes(nodes)
    .filter((node) => node.level.depth < depth)
    .sort((a, b) => pathText(a).localeCompare(pathText(b), undefined, { sensitivity: 'base' }));
}

/** "17th Street → Amazon" — a node's full place, for pickers where names repeat. */
export function pathText(node: AccessNodePublic): string {
  return node.path.map((step) => step.name).join(STR.access.catalog.levelArrow);
}

/**
 * The group a catalog row is listed under: its top-level node, "Suite applications" for an
 * app row that is not filed, and "Not filed" otherwise — the demo's grouping.
 */
export function topGroup(item: AccessCatalogItemPublic): string {
  const top = item.path[0];
  if (top !== undefined) return top.name;
  return item.application === null ? STR.access.catalog.notFiledGroup : STR.access.catalog.groupSuiteApps;
}

/** Groups in the demo's order: suite apps first, named groups by name, not filed last. */
export function groupRows<T extends AccessCatalogItemPublic>(items: readonly T[]): [string, T[]][] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = topGroup(item);
    const list = groups.get(key);
    if (list === undefined) groups.set(key, [item]);
    else list.push(item);
  }
  const rank = (key: string): number =>
    key === STR.access.catalog.groupSuiteApps ? 0 : key === STR.access.catalog.notFiledGroup ? 2 : 1;
  return [...groups.entries()]
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .map(([key, rows]): [string, T[]] => [
      key,
      [...rows].sort((a, b) => rowShort(a).localeCompare(rowShort(b))),
    ]);
}

/** A row's name within its group: its place below the top level, then its own name. */
export function rowShort(item: AccessCatalogItemPublic): string {
  const below = item.path.slice(1).map((step) => step.name);
  return [...below, item.name].join(STR.accessCommon.pathSeparator);
}
